import getLotsConstants from '@utils/lots_constants';
import { CANONICAL_COLUMN_ID, normalizeLabel } from '@features/event-level-column-rename/constants';
import { buildAcceptedHeaderNamesByColumn } from '@features/event-level-column-rename/utils/upload-column-labels';
import type { UploadColumnHeaderDefaults } from '@features/event-level-column-rename/utils/upload-column-labels';
import type { LineItemColumnLabels, TranslateLabel } from '@features/event-level-column-rename/types';

/** The standard columns the supplier quote upload reads, keyed by the quote field each one feeds. */
export const SUPPLIER_UPLOAD_COLUMN_KEY = {
  discount: 'discount',
  lineItemName: 'lineItemName',
  pricePerUnit: 'pricePerUnit',
  quantity: 'quantity',
  supplierQuantity: 'supplierQuantity',
  surcharge: 'surcharge',
} as const;

export type SupplierUploadColumnKey = keyof typeof SUPPLIER_UPLOAD_COLUMN_KEY;

export interface SupplierUploadColumnLocations {
  /** Zero-based sheet column index per column key; absent when the sheet has no such column. */
  columnIndexByColumnKey: Partial<Record<SupplierUploadColumnKey, number>>;
  /** Header texts, as the sheet carries them, that fit more than one column or repeat in row 1. */
  conflictingHeaderNames: string[];
  /** The header this event prints for each column, for messages that name a column. */
  printedHeaderNameByColumnKey: Record<SupplierUploadColumnKey, string>;
}

interface LocateSupplierUploadColumnsParams {
  companyColumnNameOverrides?: Record<string, string> | null;
  /** Row 1 of the uploaded sheet as plain text, one entry per column position (blank cells undefined). */
  headerRowCellTexts: Array<string | undefined>;
  isPriceUpdate?: boolean;
  labels?: LineItemColumnLabels | null;
  t: TranslateLabel;
}

const supplierUploadColumnHeaderDefaults = (
  t: TranslateLabel,
  isPriceUpdate: boolean
): Record<SupplierUploadColumnKey, UploadColumnHeaderDefaults> => {
  const lotsConstants = getLotsConstants(t);

  return {
    discount: { canonicalId: CANONICAL_COLUMN_ID.discountPerUnit, defaultHeaderNames: [lotsConstants.DISCOUNT_HEADER] },
    lineItemName: { canonicalId: CANONICAL_COLUMN_ID.lineItemName, defaultHeaderNames: [lotsConstants.LINE_ITEM_NAME_HEADER] },
    pricePerUnit: {
      canonicalId: CANONICAL_COLUMN_ID.pricePerUnit,
      defaultHeaderNames: isPriceUpdate
        ? [
          lotsConstants.PROPOSED_PRICE_PER_UNIT_HEADER,
          lotsConstants.PROPOSED_PRICE_HEADER,
          lotsConstants.PRICE_PER_UNIT_HEADER,
        ]
        : [lotsConstants.PRICE_PER_UNIT_HEADER],
    },
    quantity: { canonicalId: CANONICAL_COLUMN_ID.itemsPerUnit, defaultHeaderNames: [lotsConstants.ITEMS_PER_UNIT_HEADER] },
    supplierQuantity: {
      canonicalId: CANONICAL_COLUMN_ID.quotedQuantityOfUnits,
      defaultHeaderNames: [lotsConstants.QUOTED_QUANTITY_OF_UNITS_HEADER, lotsConstants.QUOTED_QUANTITY_OF_ITEMS_HEADER],
    },
    surcharge: { defaultHeaderNames: [lotsConstants.LOGISTIC_CHARGE_HEADER] },
  };
};

/**
 * Locates each standard column of an uploaded supplier quote sheet by canonical id — never by label
 * text alone. Header cells are matched against the feature's accepted header names (event label,
 * org label, default wordings); a header that fits more than one column, a column claimed by more
 * than one header, or a header repeated in row 1 is listed in `conflictingHeaderNames` and left
 * unlocated so the caller refuses the upload instead of guessing.
 */
export const locateSupplierUploadColumnsByCanonicalId = ({
  companyColumnNameOverrides,
  headerRowCellTexts,
  isPriceUpdate = false,
  labels,
  t,
}: LocateSupplierUploadColumnsParams): SupplierUploadColumnLocations => {
  const headerDefaultsByColumnKey = supplierUploadColumnHeaderDefaults(t, isPriceUpdate);
  const columnKeys = Object.keys(headerDefaultsByColumnKey) as SupplierUploadColumnKey[];
  const acceptedHeaderNamesByColumnKey = buildAcceptedHeaderNamesByColumn({
    companyColumnNameOverrides,
    headerDefaultsByColumn: headerDefaultsByColumnKey,
    labels,
    t,
  });

  const columnKeysByNormalizedHeaderName = new Map<string, SupplierUploadColumnKey[]>();
  columnKeys.forEach(columnKey => acceptedHeaderNamesByColumnKey[columnKey].acceptedHeaderNames.forEach(headerName => {
    const normalizedHeaderName = normalizeLabel(headerName);
    columnKeysByNormalizedHeaderName.set(
      normalizedHeaderName,
      [...(columnKeysByNormalizedHeaderName.get(normalizedHeaderName) ?? []), columnKey]
    );
  }));

  const conflictingHeaderNames: string[] = [];
  const matchedHeaderCellsByColumnKey = new Map<SupplierUploadColumnKey, Array<{ columnIndex: number; headerText: string }>>();

  headerRowCellTexts.forEach((headerCellText, columnIndex) => {
    if (!headerCellText) return;

    const headerText = headerCellText.trim();
    const columnKeysAcceptingHeader = columnKeysByNormalizedHeaderName.get(normalizeLabel(headerText));

    if (!columnKeysAcceptingHeader) return;
    if (columnKeysAcceptingHeader.length > 1) {
      conflictingHeaderNames.push(headerText);
      return;
    }

    const [columnKey] = columnKeysAcceptingHeader;
    matchedHeaderCellsByColumnKey.set(
      columnKey,
      [...(matchedHeaderCellsByColumnKey.get(columnKey) ?? []), { columnIndex, headerText }]
    );
  });

  const columnIndexByColumnKey: Partial<Record<SupplierUploadColumnKey, number>> = {};

  matchedHeaderCellsByColumnKey.forEach((matchedHeaderCells, columnKey) => {
    if (matchedHeaderCells.length === 1) {
      columnIndexByColumnKey[columnKey] = matchedHeaderCells[0].columnIndex;
      return;
    }

    conflictingHeaderNames.push(...matchedHeaderCells.map(headerCell => headerCell.headerText));
  });

  return {
    columnIndexByColumnKey,
    conflictingHeaderNames: conflictingHeaderNames.filter((headerText, position) => (
      conflictingHeaderNames.findIndex(otherHeaderText => normalizeLabel(otherHeaderText) === normalizeLabel(headerText)) === position
    )),
    printedHeaderNameByColumnKey: Object.fromEntries(
      columnKeys.map(columnKey => [columnKey, acceptedHeaderNamesByColumnKey[columnKey].printedHeaderName])
    ) as Record<SupplierUploadColumnKey, string>,
  };
};
